import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import React from "react";
const CallToAction = React.lazy(() => import("../components/CallToAction"))

type TagIcone = "tag" | "folha" | "coracao" | "cerebro" | "ciencia" | "sol" | "lua" | "nutricao" | "legislacao" | "entrevista";
type Tag = { id: number; Nome: string; Icone?: TagIcone };

type Blog = {
  id: number;
  Titulo?: string;
  Slug?: string;
  Resumo?: string;
  Data?: string;
  Categoria?: string;
  TempoLeitura?: number;
  Tags?: Tag[];
  Capa?: {
    url?: string;
    formats?: {
      medium?: { url?: string };
      small?: { url?: string };
    };
  };
};

function readingTime(b: Blog): number {
  if (b.TempoLeitura) return b.TempoLeitura;
  if (!b.Resumo) return 2;
  return Math.max(1, Math.round(b.Resumo.trim().split(/\s+/).length / 200));
}

function getImgSrc(b: Blog, base: string): string | undefined {
  const url = b.Capa?.formats?.medium?.url ?? b.Capa?.formats?.small?.url ?? b.Capa?.url;
  if (!url) return undefined;
  return url.startsWith("http") ? url : `${base}${url}`;
}

const CATEGORY_COLORS: Record<string, string> = {
  "Artigo":      "bg-emerald-700",
  "Notícia":     "bg-sky-600",
  "Estudo":      "bg-violet-600",
  "Dicas":       "bg-amber-500",
  "Receitas":    "bg-rose-500",
  "Entrevista":  "bg-teal-600",
};

function CategoryBadge({ label }: { label: string }) {
  const color = CATEGORY_COLORS[label] ?? "bg-primary-darker";
  return (
    <span className={`inline-block ${color} text-white text-[10px] font-sans font-semibold tracking-widest uppercase px-3 py-1 rounded-full`}>
      {label}
    </span>
  );
}

const TAG_PALETTE = [
  "bg-green-600",
  "bg-teal-500",
  "bg-sky-600",
  "bg-indigo-500",
  "bg-violet-600",
  "bg-pink-500",
  "bg-rose-500",
  "bg-orange-500",
  "bg-amber-500",
  "bg-lime-600",
];

function tagColor(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) & 0xffffffff;
  return TAG_PALETTE[Math.abs(h) % TAG_PALETTE.length];
}

function TagIcon({ icone }: { icone?: TagIcone }) {
  switch (icone) {
    case "folha":
      return <svg width="9" height="9" viewBox="0 0 14 14" fill="none"><path d="M7 13C7 13 1 9 1 4a6 6 0 0 1 12 0c0 5-6 9-6 9z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/><path d="M7 4v9" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></svg>;
    case "coracao":
      return <svg width="9" height="9" viewBox="0 0 14 14" fill="none"><path d="M7 12S1 8 1 4.5a3.5 3.5 0 0 1 6-2.45A3.5 3.5 0 0 1 13 4.5C13 8 7 12 7 12z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/></svg>;
    case "cerebro":
      return <svg width="9" height="9" viewBox="0 0 14 14" fill="none"><path d="M5 12V9a4 4 0 1 1 4 0v3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/><path d="M3 12h8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>;
    case "ciencia":
      return <svg width="9" height="9" viewBox="0 0 14 14" fill="none"><path d="M5 1v5L2 11a1 1 0 0 0 .9 1.5h8.2A1 1 0 0 0 12 11L9 6V1" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/><path d="M4 1h6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/></svg>;
    case "sol":
      return <svg width="9" height="9" viewBox="0 0 14 14" fill="none"><circle cx="7" cy="7" r="2.5" stroke="currentColor" strokeWidth="1.3"/><path d="M7 1v1.5M7 11.5V13M1 7h1.5M11.5 7H13M3 3l1 1M10 10l1 1M3 11l1-1M10 4l1-1" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/></svg>;
    case "lua":
      return <svg width="9" height="9" viewBox="0 0 14 14" fill="none"><path d="M11.5 9A5.5 5.5 0 0 1 5 2.5a5.5 5.5 0 1 0 6.5 6.5z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/></svg>;
    case "nutricao":
      return <svg width="9" height="9" viewBox="0 0 14 14" fill="none"><path d="M7 1C5 1 2 3 2 6c0 3.3 2.7 6 5 7 2.3-1 5-3.7 5-7 0-3-3-5-5-5z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/><path d="M7 4v6M5 6h4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></svg>;
    case "legislacao":
      return <svg width="9" height="9" viewBox="0 0 14 14" fill="none"><path d="M7 1v12M4 4l-3 4h6L4 4zM10 6l-3 4h6l-3-4z" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg>;
    case "entrevista":
      return <svg width="9" height="9" viewBox="0 0 14 14" fill="none"><path d="M2 2h10a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1H5l-3 2V3a1 1 0 0 1 1-1z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/></svg>;
    default:
      return <svg width="9" height="9" viewBox="0 0 10 10" fill="none"><path d="M1 1h4l4 4-4 4-4-4V1z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/><circle cx="3" cy="3" r="0.8" fill="currentColor"/></svg>;
  }
}

function TagBadge({ tag }: { tag: Tag }) {
  return (
    <span className={`inline-flex items-center gap-1 ${tagColor(tag.Nome)} text-white text-[10px] font-sans font-semibold tracking-widest uppercase px-2.5 py-1 rounded-full`}>
      <TagIcon icone={tag.Icone} />
      {tag.Nome}
    </span>
  );
}

function ArrowIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 14 14" fill="none" className="flex-shrink-0">
      <path d="M1 7h12M8 2l5 5-5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function Blogs() {
  const [blogs, setBlogs] = useState<Blog[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sortOrder, setSortOrder] = useState<"desc" | "asc">("desc");
  const [categoria, setCategoria] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [showAll, setShowAll] = useState(false);
  const base = import.meta.env.VITE_API_URL;

  useEffect(() => {
    let alive = true;
    setLoading(true);

    fetch(`${base}/api/blogs?sort=Data:${sortOrder}&populate=Capa`)
      .then((r) => r.json())
      .then((data) => { if (alive) setBlogs(data?.data ?? []); })
      .catch(() => { if (alive) setBlogs([]); })
      .finally(() => { if (alive) setLoading(false); });

    return () => { alive = false; };
  }, [base, sortOrder]);

  const categorias = useMemo(
    () => Array.from(new Set(blogs.map((b) => b.Categoria).filter(Boolean))) as string[],
    [blogs]
  );

  const filtered = useMemo(() => {
    return blogs.filter((b) => {
      if (categoria && b.Categoria !== categoria) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        const inTags = b.Tags?.some((t) => t.Nome.toLowerCase().includes(q));
        if (!b.Titulo?.toLowerCase().includes(q) && !b.Resumo?.toLowerCase().includes(q) && !inTags) return false;
      }
      return true;
    });
  }, [blogs, search, categoria]);

  // Reset carousel + grid when filters change
  useEffect(() => {
    setActiveIndex(0);
    setShowAll(false);
  }, [search, categoria, sortOrder]);

  const carouselBlogs = filtered.slice(0, 5);
  const allGridBlogs = filtered;
  const gridBlogs = showAll ? allGridBlogs : allGridBlogs.slice(0, 6);
  const hasMore = !showAll && allGridBlogs.length > 6;

  // Autoplay
  useEffect(() => {
    if (carouselBlogs.length <= 1) return;
    const id = setInterval(() => {
      setActiveIndex((i) => (i + 1) % carouselBlogs.length);
    }, 5000);
    return () => clearInterval(id);
  }, [carouselBlogs.length]);

  const fieldCls = "text-xs font-sans text-gray-600 border border-gray-200/70 rounded-lg py-1.5 bg-white/60 backdrop-blur-sm focus:outline-none focus:ring-1 focus:ring-primary-darker appearance-none"

  const filterSlot = (
    <>
      <div className="relative">
        <select
          value={sortOrder}
          onChange={(e) => setSortOrder(e.target.value as "desc" | "asc")}
          className={`${fieldCls} pl-3 pr-7 cursor-pointer`}
        >
          <option value="desc">Mais recentes</option>
          <option value="asc">Mais antigos</option>
        </select>
        <svg className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-gray-400" width="10" height="10" viewBox="0 0 10 10" fill="none">
          <path d="M2 3.5l3 3 3-3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>

      <div className="relative">
        <select
          value={categoria}
          onChange={(e) => setCategoria(e.target.value)}
          className={`${fieldCls} pl-3 pr-7 cursor-pointer`}
        >
          <option value="">Todas as categorias</option>
          {categorias.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <svg className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-gray-400" width="10" height="10" viewBox="0 0 10 10" fill="none">
          <path d="M2 3.5l3 3 3-3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>

      <div className="ml-auto relative">
        <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 z-10 text-gray-600 pointer-events-none" width="12" height="12" viewBox="0 0 14 14" fill="none">
          <circle cx="6" cy="6" r="4.5" stroke="currentColor" strokeWidth="1.5" />
          <path d="M10 10l2.5 2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
        <input
          type="text"
          placeholder="Pesquise por conteúdo..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className={`${fieldCls} pl-8 pr-3 w-48 placeholder:text-gray-600`}
        />
      </div>
    </>
  )

  return (
    <div className="min-h-screen bg-white">
      <Header filterSlot={filterSlot} />

      {/* pt = expanded header height + 48px filter slot */}
      <main className="pt-[88px] sm:pt-[112px] md:pt-[116px] lg:pt-[122px]">

        {/* ── Main content ── */}
        <div className="mx-auto max-w-6xl px-4 py-10">

          {/* Loading */}
          {loading && (
            <div className="flex justify-center py-24">
              <span className="w-9 h-9 border-2 border-primary-darker border-t-transparent rounded-full animate-spin" />
            </div>
          )}

          {/* Empty */}
          {!loading && filtered.length === 0 && (
            <p className="text-center text-gray-400 py-24 text-sm">Nenhum post encontrado.</p>
          )}

          {/* Content */}
          {!loading && filtered.length > 0 && (
            <>
              {/* ── Carousel ── */}
              <div className="relative rounded-2xl overflow-hidden mb-12 min-h-[340px] sm:min-h-[480px]">

                {/* Slides track */}
                <div
                  className="flex transition-transform duration-500 ease-in-out h-full"
                  style={{ transform: `translateX(-${activeIndex * 100}%)` }}
                >
                  {carouselBlogs.map((b) => {
                    const imgSrc = getImgSrc(b, base);
                    return (
                      <Link
                        key={b.id}
                        to={`/blogs/${b.Slug}`}
                        className="group min-w-full block relative min-h-[340px] sm:min-h-[480px] flex-shrink-0"
                      >
                        {/* Image */}
                        {imgSrc ? (
                          <img
                            src={imgSrc}
                            alt={b.Titulo ?? ""}
                            className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                            decoding="async"
                          />
                        ) : (
                          <div className="absolute inset-0 bg-primary-darker/20" />
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />

                        {/* Overlay content */}
                        <div className="relative h-full flex flex-col justify-end p-7 sm:p-12 min-h-[340px] sm:min-h-[480px]">
                          {(b.Categoria || (b.Tags && b.Tags.length > 0)) && (
                            <div className="flex flex-wrap items-center gap-2 mb-3">
                              {b.Categoria && <CategoryBadge label={b.Categoria} />}
                              {b.Tags?.map((t) => <TagBadge key={t.id} tag={t} />)}
                            </div>
                          )}
                          <h2 className="font-display font-semibold text-white text-2xl sm:text-[2.25rem] leading-tight mb-3 max-w-3xl">
                            {b.Titulo ?? "(sem título)"}
                          </h2>
                          <div className="flex items-center gap-2 text-white/65 text-[11px] font-sans mb-4">
                            {b.Data && (
                              <span>
                                {new Date(b.Data).toLocaleDateString("pt-BR", {
                                  day: "2-digit",
                                  month: "2-digit",
                                  year: "numeric",
                                })}
                              </span>
                            )}
                            {b.Data && <span>•</span>}
                            <span>{readingTime(b)} min de leitura</span>
                          </div>
                          <span className="inline-flex items-center gap-2 text-white text-sm font-sans font-medium group-hover:gap-4 transition-all duration-200">
                            Leia mais sobre <ArrowIcon />
                          </span>
                        </div>
                      </Link>
                    );
                  })}
                </div>

                {/* Dots — outside the Link slides so clicks don't navigate */}
                {carouselBlogs.length > 1 && (
                  <div className="absolute bottom-5 right-7 flex items-center gap-2 z-20">
                    {carouselBlogs.map((_, i) => (
                      <button
                        key={i}
                        type="button"
                        aria-label={`Slide ${i + 1}`}
                        onClick={() => setActiveIndex(i)}
                        className={`rounded-full transition-all duration-300 ${
                          i === activeIndex
                            ? "w-5 h-2 bg-white"
                            : "w-2 h-2 bg-white/50 hover:bg-white/80"
                        }`}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* ── Grid ── */}
              {gridBlogs.length > 0 && (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-10 mb-12">
                    {gridBlogs.map((b) => {
                      const imgSrc = getImgSrc(b, base);
                      return (
                        <Link
                          key={b.id}
                          to={`/blogs/${b.Slug}`}
                          className="group flex flex-col"
                        >
                          {/* Thumbnail */}
                          <div className="relative aspect-[4/3] overflow-hidden rounded-xl bg-gray-100 mb-3">
                            {imgSrc ? (
                              <img
                                src={imgSrc}
                                alt={b.Titulo ?? ""}
                                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                                loading="lazy"
                                decoding="async"
                                draggable={false}
                              />
                            ) : (
                              <div className="h-full w-full bg-primary-darker/10" />
                            )}
                            {b.Categoria && (
                              <div className="absolute top-3 left-3">
                                <CategoryBadge label={b.Categoria} />
                              </div>
                            )}
                          </div>

                          {/* Text */}
                          <h2 className="font-display font-semibold text-gray-900 text-[15px] leading-snug mb-1.5 group-hover:text-primary-darker transition-colors line-clamp-2">
                            {b.Titulo ?? "(sem título)"}
                          </h2>
                          <p className="text-gray-400 text-[11px] font-sans mb-3 leading-none">
                            {b.Data && new Date(b.Data).toLocaleDateString("pt-BR", {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                            })}
                            {b.Data && "  •  "}
                            {readingTime(b)} min de leitura
                          </p>
                          {b.Tags && b.Tags.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mb-3">
                              {b.Tags.map((t) => <TagBadge key={t.id} tag={t} />)}
                            </div>
                          )}
                          <span className="inline-flex items-center gap-1.5 text-primary-darker text-[13px] font-sans font-medium group-hover:gap-3 transition-all duration-200">
                            Leia mais sobre <ArrowIcon />
                          </span>
                        </Link>
                      );
                    })}
                  </div>

                  {/* Load all button */}
                  {hasMore && (
                    <div className="flex justify-center">
                      <button
                        type="button"
                        onClick={() => setShowAll(true)}
                        className="inline-flex items-center gap-2 border border-gray-300 text-gray-700 text-sm font-sans font-medium px-8 py-3 rounded-full hover:border-primary-darker hover:text-primary-darker transition-colors duration-200"
                      >
                        Carregar todos
                        <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                          <path d="M7 1v12M2 9l5 5 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>

        <section className="mx-4 py-10">
          <CallToAction buttonLabel="Iniciar agora" href="#protocolo" />
        </section>

        <Footer />
      </main>
    </div>
  );
}
